import React, { memo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs } from 'antd';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { ButtonV2 } from 'src/components/general/button/lgg-button';
import {
  EmptyListIconContainer,
  EmptyListMessage,
  EmptyListPlaceholder,
  EmptyListTitle,
} from 'src/components/general/feedback/empty-list-placeholder';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';

const SOURCE_ICONS = {
  websites: 'world',
  documents: 'document',
  qa: 'chat',
} as const;

type SourceKey = keyof typeof SOURCE_ICONS;

const SOURCE_KEYS = Object.keys(SOURCE_ICONS) as SourceKey[];

const StyledTabs = styled(Tabs)<{ $tabHeight: number }>`
  .ant-tabs-nav {
    margin: 0;

    &::before {
      border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
    }

    .ant-tabs-ink-bar {
      background-color: ${({ theme }) => theme.colors.gogo};
      height: 2px;
    }
  }

  .ant-tabs-tab {
    color: ${({ theme }) => theme.colors.flint};
    font-family: ${({ theme }) => theme.font.regular};
    font-size: 13px;
    height: ${({ $tabHeight }) => $tabHeight}px;
    justify-content: center;
    line-height: 16px;
    padding: 10px;

    ${up('md')} {
      width: 250px;
    }

    &:hover {
      color: ${({ theme }) => theme.colors.flint};
    }

    & + .ant-tabs-tab {
      margin: 0;
    }

    &.ant-tabs-tab-active .ant-tabs-tab-btn {
      color: ${({ theme }) => theme.colors.gogo};
      font-family: ${({ theme }) => theme.font.medium};
      line-height: 16px; /* 123.077% */
      letter-spacing: -0.13px;

      font-style: normal;
      font-weight: 500;
      text-shadow: none;
      -webkit-text-stroke: 0;
    }
  }
`;

const EmptyState = styled(FlexColumn)`
  align-items: center;
  gap: 17px;
  padding: 60px 20px;

  ${up('md')} {
    padding-top: 184px;
  }
`;

const SourceEmptyPlaceholder = styled(EmptyListPlaceholder)`
  margin-top: 0;

  ${EmptyListIconContainer} {
    background-color: ${({ theme }) => theme.colors.porcelain2};
    height: 82px;
    margin-bottom: 17px;
    width: 82px;

    svg path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }

  ${EmptyListTitle} {
    color: ${({ theme }) => theme.colors.steel};
    font-size: 18px;
    letter-spacing: -0.36px;
    line-height: 23px;
    margin: 0 0 1px;
  }

  ${EmptyListMessage} {
    line-height: 20px;
    max-width: 395px;
  }
`;

export const KnowledgeEmptyState = memo<{
  testId: string;
  icon: string;
  title: string;
  message: string;
  buttonLabel?: string;
  onAdd?: VoidFunction;
}>(({ testId, icon, title, message, buttonLabel, onAdd }) => (
  <EmptyState data-lgg-id={testId}>
    <SourceEmptyPlaceholder
      title={title}
      message={message}
      iconConfig={{ type: 'embedded', icon }}
    />
    {buttonLabel && (
      <ButtonV2
        variant="primary"
        size="small"
        disabled={!onAdd}
        onClick={onAdd}
        data-lgg-id={`${testId}-add-button`}
      >
        {buttonLabel}
      </ButtonV2>
    )}
  </EmptyState>
));

export const KnowledgeSourceEmptyState = memo<{
  sourceKey: SourceKey;
  canAdd?: boolean;
  onAdd?: VoidFunction;
}>(({ sourceKey, canAdd = true, onAdd }) => {
  const { t } = useTranslation(['aiAgents']);

  return (
    <KnowledgeEmptyState
      testId={`knowledge-${sourceKey}-empty`}
      icon={SOURCE_ICONS[sourceKey]}
      title={t(`aiAgents:knowledge.empty.${sourceKey}.title`)}
      message={t(`aiAgents:knowledge.empty.${sourceKey}.message`)}
      buttonLabel={canAdd ? t(`aiAgents:knowledge.empty.${sourceKey}.button`) : undefined}
      onAdd={onAdd}
    />
  );
});

export const KnowledgeSourceContainer = styled.div`
  padding: 20px 15px;

  ${up('md')} {
    padding: 20px;
  }
`;

const SourceHeader = styled(FlexRow)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const SourceCount = styled.span`
  color: ${({ theme }) => theme.colors.storm};
  font-size: 15px;
  line-height: 19px;

  strong {
    color: ${({ theme }) => theme.colors.gogo};
    font-weight: normal;
  }
`;

export const KnowledgeSourceHeader = memo<{
  testId: string;
  count: number;
  countLabel: string;
  addLabel: string;
  addDisabled?: boolean;
  onAdd?: VoidFunction;
}>(({ testId, count, countLabel, addLabel, addDisabled, onAdd }) => (
  <SourceHeader>
    <SourceCount data-lgg-id={`${testId}-count`}>
      <strong>{count}</strong> {countLabel}
    </SourceCount>
    {onAdd && (
      <ButtonV2
        data-lgg-id={`${testId}-add-button`}
        variant="cta"
        size="small"
        disabled={addDisabled}
        onClick={onAdd}
      >
        {addLabel}
      </ButtonV2>
    )}
  </SourceHeader>
));

type KnowledgeTabsProps = {
  testId: string;
  tabHeight: 38 | 58;
  extraTabs?: { key: string; label: string; content: ReactNode }[];
  sourceContent?: Partial<Record<SourceKey, ReactNode>>;
};

export const KnowledgeTabs = memo<KnowledgeTabsProps>(
  ({ testId, tabHeight, extraTabs = [], sourceContent = {} }) => {
    const { t } = useTranslation(['aiAgents']);

    return (
      <StyledTabs data-lgg-id={testId} $tabHeight={tabHeight}>
        {SOURCE_KEYS.map((key) => (
          <Tabs.TabPane key={key} tab={t(`aiAgents:knowledge.tabs.${key}`)}>
            {sourceContent[key] ?? <KnowledgeSourceEmptyState sourceKey={key} />}
          </Tabs.TabPane>
        ))}
        {extraTabs.map(({ key, label, content }) => (
          <Tabs.TabPane key={key} tab={label}>
            {content}
          </Tabs.TabPane>
        ))}
      </StyledTabs>
    );
  },
);
